import PropTypes from 'prop-types'
import React from 'react'
import { makeStyles } from '@material-ui/core'
import FadeIn from '../../../../../molecules/Transitions/FadeIn'
import { extractTextContent, parseAttachmentNames, resolveMessageRole, stripAttachmentBlocks } from '../message/messageUtils'
import { useAgentPanelContext } from '../AgentPanelContext'
import MessageHeader from '../message/MessageHeader'
import MessageBody from '../message/MessageBody'
import ProposedContentCard from '../blocks/ProposedContentCard'

const TOOL_ICONS = {
  search_blueprints: '🔍',
  search_data_pipelines: '🔍',
  search_knowledge_base: '🔍',
  get_component_registry: '📦',
  get_component_docs: '📄',
  get_query_blueprint: '📋',
  execute_query_blueprint: '▶',
  get_pipeline_schema: '📊',
  execute_data_pipeline: '▶',
  list_views: '📁',
  get_view_edit_details: '📁',
  list_view_versions: '📁',
  get_view_version: '📁',
  save_view_version: '💾',
  propose_view_content: '✨'
}

const useStyles = makeStyles(() => ({
  toolChip: {
    color: 'var(--color-neutral-600)',
    background: 'var(--surface-bg-color-d1)',
    padding: '2px 8px',
    lineHeight: 1.4
  },
  toolIcon: {
    fontSize: '10px',
    lineHeight: 1
  }
}))

function ToolChips ({ toolCalls, toolMessages }) {
  const classes = useStyles()
  if (!toolCalls.length) return null

  const seen = new Set()
  const deduplicated = toolCalls.filter(call => {
    if (seen.has(call.name)) return false
    seen.add(call.name)
    return true
  })

  return (
    <div className='flex gap-xs flex-wrap'>
      {deduplicated.map(call => {
        const label = toolMessages?.[call.name] || call.name
        const icon = TOOL_ICONS[call.name]
        return (
          <span key={call.id || call.name} className={`${classes.toolChip} inline-flex items-center gap-1 font-size-xs radius-sm`}>
            {icon && <span className={classes.toolIcon}>{icon}</span>}
            {label.replace(/\.\.\.$/g, '')}
          </span>
        )
      })}
    </div>
  )
}

ToolChips.propTypes = {
  toolCalls: PropTypes.array.isRequired,
  toolMessages: PropTypes.object
}

function ChatMessage ({ message }) {
  const { config } = useAgentPanelContext()
  const safeMessage = message || {}
  const role = resolveMessageRole(safeMessage)

  if (!message || role === 'tool') return null

  const rawText = extractTextContent(safeMessage.content || safeMessage.message)
  const textContent = stripAttachmentBlocks(rawText)

  if (role === 'system') {
    return (
      <FadeIn className='py-2'>
        <div className='font-size-xs opacity-lowlighted text-center'>
          {textContent}
        </div>
      </FadeIn>
    )
  }
  const attachmentNames = parseAttachmentNames(safeMessage.content)
  const isGenerating = safeMessage.isGenerating || safeMessage.generationStatus === 'running'
  const createdDate = safeMessage.createdDate || safeMessage.createdAt || new Date().toISOString()

  const toolCalls = Array.isArray(safeMessage.toolCalls) ? safeMessage.toolCalls : []
  const proposedContents = toolCalls.filter(c => c.name === 'propose_view_content' && c.args?.content)
  const otherToolCalls = toolCalls.filter(c => c.name !== 'propose_view_content')

  const hasTextContent = textContent && textContent.trim().length > 0
  const hasProposedContent = proposedContents.length > 0
  const hasToolCalls = otherToolCalls.length > 0
  const hasAttachments = attachmentNames.length > 0
  const isToolOnlyMessage = !hasTextContent && !hasProposedContent && hasToolCalls && !isGenerating

  const bodyClassName = role === 'user'
    ? 'bg-neutral-050 p-sm shadow-md text-contrast-neutral-050 radius-sm flex flex-col gap-sm'
    : 'py-sm radius-sm flex flex-col gap-sm'

  if (isToolOnlyMessage) {
    return (
      <FadeIn className='py-2 radius-sm'>
        <ToolChips toolCalls={otherToolCalls} toolMessages={config.toolMessages} />
      </FadeIn>
    )
  }

  return (
    <div>
      <MessageHeader role={role} agentLabel={config.label} createdDate={createdDate} />
      <FadeIn className={bodyClassName}>
        {hasTextContent && (
          <MessageBody
            text={textContent}
            isGenerating={isGenerating}
            generationStatus={safeMessage.generationStatus}
            errorMessage={safeMessage.errorMessage}
          />
        )}
        {!hasTextContent && isGenerating && (
          <MessageBody
            text=''
            isGenerating
            generationStatus={safeMessage.generationStatus}
            errorMessage={safeMessage.errorMessage}
          />
        )}
        {hasAttachments && (
          <div className='flex gap-xs flex-wrap'>
            {attachmentNames.map((name) => (
              <span key={name} className='font-size-xs opacity-lowlighted'>
                Attachment: {name}
              </span>
            ))}
          </div>
        )}
        {proposedContents.length > 0 && proposedContents.map((call, idx) => (
          <ProposedContentCard
            key={call.id || `proposed-${idx}`}
            content={call.args?.content || ''}
            description={call.args?.description}
          />
        ))}
        {hasToolCalls && (
          <ToolChips toolCalls={otherToolCalls} toolMessages={config.toolMessages} />
        )}
      </FadeIn>
    </div>
  )
}

ChatMessage.propTypes = {
  message: PropTypes.shape({
    role: PropTypes.string,
    actor: PropTypes.string,
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    isGenerating: PropTypes.bool,
    generationStatus: PropTypes.string,
    toolCalls: PropTypes.array,
    createdDate: PropTypes.string,
    createdAt: PropTypes.string,
    errorMessage: PropTypes.string
  })
}

export default ChatMessage
